import { TFunction } from 'i18next';

import {
  IExtendedSpecificationValue,
  SpecificationGroupItemType,
  SpecificationsValueItemType,
} from '@/interfaces/cars.interfaces';
import sortCollectionItems from '@/utils/sortCollectionItems';

type SpecificationInfoType = {
  key: string;
  label: string;
  value?: string;
  group: string;
  position?: number;
};

type SpecificationGroupsType = {
  id: string;
  title: string;
  items: Array<SpecificationInfoType>;
  position?: number;
};

/**
 * Формирует данные для вкладки "Технические характеристики"
 * @param specifications значения технических характеристик автомобиля
 * @param specificationsDictionary справочник технических характеристик
 * @param specificationsDictionary справочник групп технических характеристик
 */
function formatSpecificationGroups(
  specifications: Array<IExtendedSpecificationValue>,
  specificationsDictionary: SpecificationsValueItemType[],
  groupsDictionary: SpecificationGroupItemType[],
  translations: TFunction<'translation', undefined>,
) {
  const specificationInfo: Array<SpecificationInfoType> = [];

  specifications.forEach(spec => {
    const dictionaryValue = specificationsDictionary.find(
      dictEntry => dictEntry.id === spec.specification,
    );

    if (!dictionaryValue) {
      return;
    }

    let specValue: string | null = '';

    switch (dictionaryValue.data.dataType) {
      case 'boolean':
        specValue = spec.booleanValue
          ? translations('booleanValues.trueValue')
          : translations('booleanValues.falseValue');
        break;
      case 'number':
        specValue = spec.numberValue?.toLocaleString() || null;
        break;
      default:
        specValue = spec.stringValue ?? null;
    }

    if (!dictionaryValue?.data.group || !specValue) {
      return;
    }

    const specInfo = {
      key: spec.id,
      label: dictionaryValue.data.alternateName || dictionaryValue.data.name,
      value: specValue,
      group: dictionaryValue.data.group,
      position: dictionaryValue.data.position,
    };

    specificationInfo.push(specInfo);
  });

  const specsByGroups: Array<SpecificationGroupsType> = groupsDictionary.reduce(
    (acc: Array<SpecificationGroupsType>, group) => {
      const specsByGroup = specificationInfo
        .filter(info => info.group === group.id)
        .sort(sortCollectionItems);

      if (!specsByGroup.length) {
        return acc;
      }

      return [
        ...acc,
        {
          id: group.id,
          title: group.data.alternateName || group.data.name,
          items: specsByGroup,
          position: group.data.position,
        },
      ];
    },
    [],
  );

  return specsByGroups.sort(sortCollectionItems);
}

export default formatSpecificationGroups;
